/**
 * Quote leveling board — makes commercial comparison explicit.
 *
 * Displays the four separately-reported answers (lowest submitted /
 * normalized / complete / responsive-and-qualified) and, per quote, WHY they
 * differ: disqualification reasons, scope gaps, exclusions, unknowns, and
 * evidenced normalization adjustments. This is deliberately not a score
 * leaderboard — a cheap non-responsive bid is shown disqualified, with its
 * omissions named, exactly as the backend reports it.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import {
  fmtMoney,
  presentLowestKeys,
  quoteDistinctions,
  COMPLIANCE_LABELS,
} from "./levelingUtils";
import {
  Trophy,
  Ban,
  AlertTriangle,
  HelpCircle,
  Scale,
  FileWarning,
  CalendarX2,
  CalendarCheck2,
} from "lucide-react";

const bidderName = (leveling, quoteId) =>
  leveling?.quotes?.find((q) => q.quoteId === quoteId)?.bidder || null;

const QuoteCard = ({ quote, leveling, recommendedQuoteId }) => {
  const distinctions = quoteDistinctions(leveling, quote.quoteId);
  const isRecommended = recommendedQuoteId && quote.quoteId === recommendedQuoteId;
  const isLowestSubmitted = leveling?.lowestSubmittedQuoteId === quote.quoteId;

  return (
    <Card
      className={`border ${
        isRecommended
          ? "border-emerald-500/50 bg-emerald-500/5"
          : quote.disqualified
            ? "border-red-500/30"
            : "border-border"
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{quote.bidder}</CardTitle>
            <p className="text-xs text-muted-foreground">Revision {quote.revision}</p>
          </div>
          {isRecommended && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1" variant="outline">
              <Trophy className="w-3 h-3" />
              RECOMMENDED
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Submitted</p>
            <p className="text-2xl font-bold">
              {fmtMoney(quote.submittedAmount) ?? <UnknownValue reason="No submitted amount on record." />}
            </p>
          </div>
          {quote.normalizedAmount !== null &&
            quote.normalizedAmount !== undefined &&
            quote.normalizedAmount !== quote.submittedAmount && (
              <div>
                <p className="text-xs text-muted-foreground">Normalized (evaluated)</p>
                <p className="text-lg font-semibold text-blue-400">{fmtMoney(quote.normalizedAmount)}</p>
              </div>
            )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {distinctions.map((d) => (
            <Badge
              key={d}
              variant="outline"
              className={
                d === "Lowest responsive & qualified"
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-medium"
                  : "bg-blue-500/15 text-blue-400 border-blue-500/25 font-normal"
              }
            >
              {d.toUpperCase()}
            </Badge>
          ))}
          {quote.scheduleCompliant === false && (
            <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/25 gap-1 font-normal">
              <CalendarX2 className="w-3 h-3" />
              Schedule non-compliant
            </Badge>
          )}
          {quote.scheduleCompliant === true && (
            <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25 gap-1 font-normal">
              <CalendarCheck2 className="w-3 h-3" />
              Meets schedule
            </Badge>
          )}
          {quote.proposedDurationDays !== null && quote.proposedDurationDays !== undefined && (
            <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-border font-normal">
              {quote.proposedDurationDays} days
            </Badge>
          )}
          <Badge variant="outline" className="bg-muted/50 text-muted-foreground border-border font-normal">
            {COMPLIANCE_LABELS[quote.complianceState] || quote.complianceState}
          </Badge>
        </div>

        {quote.disqualified && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 space-y-1">
            <p className="text-xs font-semibold text-red-400 flex items-center gap-1">
              <Ban className="w-3.5 h-3.5" />
              DISQUALIFIED
              {isLowestSubmitted ? " — despite being lowest submitted" : ""}
            </p>
            {(quote.disqualificationReasons || []).map((r, i) => (
              <p key={i} className="text-xs text-red-300/90">
                {r}
              </p>
            ))}
          </div>
        )}

        {!quote.disqualified && (quote.scopeGaps || []).length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-1">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
              <FileWarning className="w-3.5 h-3.5" />
              Scope gaps — not complete
            </p>
            {quote.scopeGaps.map((g, i) => (
              <p key={i} className="text-xs text-amber-300/90">
                {g}
              </p>
            ))}
          </div>
        )}

        {(quote.commercialExceptions || []).length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Exclusions & qualifications</p>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
              {quote.commercialExceptions.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {(quote.unknowns || []).length > 0 && (
          <div className="rounded-lg bg-muted/30 p-2.5 space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <HelpCircle className="w-3.5 h-3.5" />
              Unknown — needs the operator, not a guess
            </p>
            {quote.unknowns.map((u, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {u}
              </p>
            ))}
          </div>
        )}

        {(quote.normalizationAdjustments || []).length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Scale className="w-3.5 h-3.5" />
              Normalization adjustments (evidenced)
            </p>
            {quote.normalizationAdjustments.map((a, i) => (
              <div key={i} className="text-xs text-muted-foreground rounded bg-muted/30 px-2 py-1">
                <span className="font-medium">{fmtMoney(a.amount)}</span> — {a.reason}
                {a.evidence && <span className="block text-[10px] opacity-80">Evidence: {a.evidence}</span>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const LevelingBoard = ({ leveling, recommendedQuoteId }) => {
  if (!leveling) return null;
  const quotes = leveling.quotes || [];

  return (
    <div className="space-y-4">
      {/* The separately-reported answers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {presentLowestKeys(leveling).map(({ key, label, hint }) => {
          const winner = bidderName(leveling, leveling[key]);
          const isFinal = key === "lowestResponsiveQualifiedQuoteId";
          return (
            <Card key={key} className={`border ${isFinal ? "border-emerald-500/40" : "border-border"}`} title={hint}>
              <CardContent className="py-3 px-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className={`text-sm font-semibold mt-0.5 ${isFinal ? "text-emerald-400" : ""}`}>
                  {winner || <UnknownValue reason="No quote qualifies for this classification." />}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(leveling.classificationNotes || []).length > 0 && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="py-3 px-4 space-y-1">
            <p className="text-xs font-semibold text-blue-400">Why the answers differ</p>
            {leveling.classificationNotes.map((n, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {n}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {quotes.map((q) => (
          <QuoteCard key={q.quoteId} quote={q} leveling={leveling} recommendedQuoteId={recommendedQuoteId} />
        ))}
      </div>

      {(leveling.missingInformation || []).length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-3 px-4 space-y-1">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              Missing information
            </p>
            {leveling.missingInformation.map((m, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {m}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LevelingBoard;
